import { FontAwesome } from '@expo/vector-icons';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { TouchableHighlight } from 'react-native';
import colors from '../../../config/colors';
import Typography, { TextProps } from '../Typography/Typography.component';

interface ReportDayProps {
	day: number;
	reportedTime: string;
	entrances?: number;
	exits?: number;
	isReportAcceptable?: boolean;
	isDaySelected?: boolean;
	isDisabled?: boolean;
	isCurrentDate?: boolean;
	dateTitle?: string;
	onPress?: () => void;
	showAsDisabled?: boolean;
	notDisplayTotalHours?: boolean;
}

const ReportsCalendarDay = ({
	day,
	entrances,
	exits,
	isDisabled = false,
	isDaySelected = false,
	isCurrentDate = false,
	isReportAcceptable,
	dateTitle,
	showAsDisabled,
	onPress = () => {},
}: ReportDayProps) => {
	const windowHeight = useWindowDimensions().height;
	const [dateTypographyOptions, setDateTypographyOptions] = useState<
		Partial<TextProps>
	>();

	useEffect(() => {
		setDateTypographyOptions({
			color: isDaySelected ? 'white' : isCurrentDate ? 'titlePrimary' : 'primaryDarkBlue',
			weight: isDisabled || showAsDisabled ? '400' : 'normal',
		});
	}, [isDisabled, isCurrentDate, isDaySelected, showAsDisabled]);

	const dateContainerStyle = StyleSheet.flatten([
		styles.dateContainer,
		isDisabled || showAsDisabled ? styles.disabledContainer : {},
		isCurrentDate ? styles.currentDayContainer : {},
		isDaySelected ? styles.selectedDay : {},
	]);

	const rootS = StyleSheet.flatten([
		styles.root,
		windowHeight >= 1000 ? { height: 86 } : { height: 56 },
	]);
	return (
		<View style={rootS}>
			<Typography size={10} style={styles.dateTitle}>
				{dateTitle}
			</Typography>
			<TouchableHighlight
				disabled={isDisabled}
				underlayColor='transparent'
				onPress={onPress}
			>
				<View style={dateContainerStyle}>
					<Typography {...dateTypographyOptions}>{day}</Typography>
					{(() => {
						const showRed = typeof isReportAcceptable === 'boolean' && !isReportAcceptable;
						const showGreen =
							!showRed &&
							typeof entrances === 'number' &&
							typeof exits === 'number' &&
							entrances > 0 &&
							entrances === exits;
						if (showRed) return <View style={[styles.dot, styles.redDot]} />;
						if (showGreen) return <View style={[styles.dot, styles.greenDot]} />;
						return null;
					})()}
				</View>
			</TouchableHighlight>
		</View>
	);
};

const styles = StyleSheet.create({
	root: {
		height: 86,
		width: 44,
		marginHorizontal: 8,
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
	},
	dateContainer: {
		position: 'relative',
		backgroundColor: colors.white,
		height: 40,
		width: 40,
		borderRadius: 8,
		borderColor: colors.datepickerSeparator,
		borderWidth: 1,
		justifyContent: 'center',
		alignItems: 'center',
	},
	dateTitle: {
		textAlign: 'center',
		height: 24,
	},
	disabledContainer: {
		backgroundColor: colors.white,
		borderColor: colors.datepickerSeparator,
		borderWidth: 1,
	},
	selectedDay: {
		backgroundColor: colors.titlePrimary,
		borderColor: colors.titlePrimary,
	},
	currentDayContainer: {
		backgroundColor: '#EDF8FF',
		borderColor: colors.titlePrimary,
		borderWidth: 1,
	},
	dot: {
		position: 'absolute',
		bottom: 4,
		width: 6,
		height: 6,
		borderRadius: 3,
	},
	greenDot: {
		backgroundColor: colors.greenClock,
	},
	redDot: {
		backgroundColor: colors.redClock,
	},
	reportsCountContainer: {
		flexDirection: 'row',
		justifyContent: 'space-evenly',
		width: '100%',
		height: 12,
		marginTop: 8,
	},
	signIn: {
		fontSize: 10,
		color: colors.lightPrimary,
	},
	signOut: {
		fontSize: 10,
		color: colors.warning,
		transform: [{ rotateY: '180deg' }],
	},
});

export default ReportsCalendarDay;
